import { useEffect, useState } from "react";
import { useAppDispatch } from "hooks/useAppDispatch";
import { registerThunk } from "reduxStore/auth/thunks";
import { ROLES } from "types/user";
import {
  fetchUsersMissingFromCognito,
  MissingCognitoUser,
} from "services/cognitoProvisioning";

type LoadingData = { current: number; total: number };

type ReportData = {
  success: string[];
  error: string[];
};

export const useProvisionCognitoUsers = (open: boolean) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [users, setUsers] = useState<MissingCognitoUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState<LoadingData>({
    current: 0,
    total: 0,
  });
  const [report, setReport] = useState<ReportData>({
    success: [],
    error: [],
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setCompleted(false);
    setReport({ success: [], error: [] });
    setLoadingData({ current: 0, total: 0 });

    const loadUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchUsersMissingFromCognito();
        setUsers(response.users);
      } catch {
        setError("Unable to load users missing from Cognito.");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [open]);

  const provisionUsers = async (sendInvite: boolean) => {
    if (users.length === 0) {
      return;
    }

    setProvisioning(true);
    setCompleted(false);
    setError(null);

    const successElements: string[] = [];
    const errorElements: string[] = [];

    setLoadingData({ current: 0, total: users.length });

    for (const user of users) {
      const result = await dispatch(
        registerThunk({
          user_id: user.user_id,
          email: user.email,
          name: user.name,
          role: user.role as ROLES,
          sendInvite,
        }),
      );

      if (registerThunk.fulfilled.match(result)) {
        successElements.push(user.email);
      } else {
        errorElements.push(user.email);
      }

      setLoadingData((prev) => ({
        current: prev.current + 1,
        total: prev.total,
      }));
    }

    setReport({
      success: successElements,
      error: errorElements,
    });
    setProvisioning(false);
    setCompleted(true);
  };

  const reset = () => {
    setCompleted(false);
    setReport({ success: [], error: [] });
    setLoadingData({ current: 0, total: 0 });
  };

  return {
    loading,
    provisioning,
    completed,
    users,
    error,
    loadingData,
    report,
    provisionUsers,
    reset,
  };
};
